import React, { useState, useCallback, useEffect, useRef } from 'react';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { Badge, Btn, ProgressBar, Select } from '../components/UI';
import { useStore } from '../data/store';
import { useToast } from '../components/Toast';
import supabase from '../data/supabase';
import CampaignsPanel from '../components/Campaigns';

/* ═══════════════════════════════════════════
   WORKFLOW BUILDER — HubSpot-Level
   Visual branching, trigger config, enrollment
   filters, goal-based exit, tree data model
   ═══════════════════════════════════════════ */

const crd = { background: T.card, border: "1px solid " + T.bdr, borderRadius: 8 };
const lbl = { fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 };
const inpS = { background: T.bg, border: "1px solid " + T.bdr, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: T.txt, outline: "none", fontFamily: "inherit", width: "100%" };

// ── TRIGGERS ──
const TRIGGERS = [
  { id:"contact_added", label:"Contact created", icon:"plus", cat:"Contacts", desc:"When a new contact is added",
    fields:[{ key:"source", label:"From source", type:"select", options:["Any","Manual","Import","LinkedIn","Form","API"] }] },
  { id:"contact_property", label:"Property changed", icon:"edit", cat:"Contacts", desc:"When a contact field changes",
    fields:[
      { key:"property", label:"Property", type:"select", options:["Lead Stage","Score","Status","Owner","Industry","City","Custom..."] },
      { key:"value", label:"New value equals", type:"text", placeholder:"Leave blank for any change" },
    ] },
  { id:"score_threshold", label:"Score threshold", icon:"target", cat:"Contacts", desc:"When lead score crosses a value",
    fields:[
      { key:"operator", label:"When score", type:"select", options:["is above","is below","equals"] },
      { key:"value", label:"Value", type:"text", placeholder:"e.g. 80" },
    ] },
  { id:"tag_added", label:"Tag added", icon:"tag", cat:"Contacts", desc:"When a tag is applied to contact",
    fields:[{ key:"tag", label:"Tag", type:"text", placeholder:"e.g. hot-lead" }] },
  { id:"deal_created", label:"Deal created", icon:"briefcase", cat:"Deals", desc:"When a new deal is added",
    fields:[{ key:"pipeline", label:"In pipeline", type:"select", options:["Any","Sales Pipeline","Enterprise","Custom..."] }] },
  { id:"deal_stage", label:"Deal stage changed", icon:"arrowR", cat:"Deals", desc:"When a deal moves stages",
    fields:[
      { key:"from_stage", label:"From stage", type:"select", options:["Any","Lead","Discovery","Qualification","Proposal","Negotiation","Closed Won","Closed Lost"] },
      { key:"to_stage", label:"To stage", type:"select", options:["Any","Lead","Discovery","Qualification","Proposal","Negotiation","Closed Won","Closed Lost"] },
    ] },
  { id:"deal_won", label:"Deal won", icon:"star", cat:"Deals", desc:"When a deal is marked won", fields:[] },
  { id:"deal_lost", label:"Deal lost", icon:"x", cat:"Deals", desc:"When a deal is marked lost", fields:[] },
  { id:"deal_inactive", label:"Deal inactive", icon:"alert", cat:"Deals", desc:"No activity for X days",
    fields:[{ key:"days", label:"Inactive for (days)", type:"text", placeholder:"14" }] },
  { id:"email_opened", label:"Email opened", icon:"eye", cat:"Email", desc:"When a sent email is opened", fields:[] },
  { id:"email_replied", label:"Email replied", icon:"mail", cat:"Email", desc:"When contact replies", fields:[] },
  { id:"email_bounced", label:"Email bounced", icon:"alert", cat:"Email", desc:"When an email bounces", fields:[] },
  { id:"call_completed", label:"Call completed", icon:"phone", cat:"Dialer", desc:"After a call ends",
    fields:[
      { key:"min_duration", label:"Min duration (sec)", type:"text", placeholder:"0" },
      { key:"outcome", label:"Outcome", type:"select", options:["Any","Connected","Voicemail","No Answer","Busy"] },
    ] },
  { id:"task_overdue", label:"Task overdue", icon:"alert", cat:"Tasks", desc:"When a task passes its due date", fields:[] },
  { id:"task_completed", label:"Task completed", icon:"check", cat:"Tasks", desc:"When a task is marked done", fields:[] },
  { id:"date_trigger", label:"Date-based", icon:"calendar", cat:"Time", desc:"On a specific date or recurring",
    fields:[
      { key:"type", label:"Type", type:"select", options:["Specific date","Days before close date","Days after created","Recurring weekly","Recurring monthly"] },
      { key:"value", label:"Value", type:"text", placeholder:"e.g. 7 (days)" },
      { key:"time", label:"Time", type:"select", options:["9:00 AM","10:00 AM","11:00 AM","12:00 PM","1:00 PM","2:00 PM","3:00 PM","4:00 PM","5:00 PM"] },
    ] },
  { id:"form_submitted", label:"Form submitted", icon:"note", cat:"Forms", desc:"When a contact fills a form",
    fields:[{ key:"form", label:"Form", type:"select", options:["Any","Contact Form","Demo Request","Newsletter","Custom..."] }] },
  { id:"manual_enroll", label:"Manual enrollment", icon:"plus", cat:"Manual", desc:"Manually add contacts", fields:[] },
];

// ── ACTIONS ──
const ACTIONS = [
  { id:"send_email", label:"Send email", icon:"Em", cat:"Communication", desc:"Send a personalized email",
    fields:[
      { key:"template", label:"Template", type:"select", options:["Write custom","Initial Outreach","Follow-up","Proposal","Re-engagement","Meeting Request","Thank You","Check-in"] },
      { key:"subject", label:"Subject", type:"text", placeholder:"Hey {{first_name}}, quick question" },
      { key:"body", label:"Body", type:"email_body", placeholder:"Hi {{first_name}},\n\n..." },
      { key:"from", label:"From", type:"select", options:["Contact owner","Workflow creator","Custom..."] },
    ] },
  { id:"send_sms", label:"Send SMS", icon:"send", cat:"Communication", desc:"Send a text message",
    fields:[{ key:"message", label:"Message", type:"textarea", placeholder:"Hi {{first_name}}, ..." }] },
  { id:"create_task", label:"Create task", icon:"check", cat:"Tasks", desc:"Assign a task to someone",
    fields:[
      { key:"title", label:"Title", type:"text", placeholder:"Follow up with {{first_name}}" },
      { key:"assignee", label:"Assign to", type:"select", options:["Contact owner","Workflow creator","Round robin","Specific user..."] },
      { key:"priority", label:"Priority", type:"select", options:["High","Medium","Low"] },
      { key:"due", label:"Due in", type:"select", options:["1 day","2 days","3 days","5 days","1 week","2 weeks"] },
    ] },
  { id:"update_property", label:"Set property", icon:"edit", cat:"Contacts", desc:"Update a contact field",
    fields:[
      { key:"property", label:"Property", type:"select", options:["Lead Stage","Status","Score","Owner","Source","Industry","Custom..."] },
      { key:"value", label:"Value", type:"text", placeholder:"New value" },
    ] },
  { id:"add_score", label:"Adjust score", icon:"target", cat:"Contacts", desc:"Add or subtract lead score",
    fields:[
      { key:"operation", label:"Operation", type:"select", options:["+  Add points","−  Subtract points","=  Set to exact value"] },
      { key:"points", label:"Points", type:"text", placeholder:"10" },
    ] },
  { id:"change_stage", label:"Move deal stage", icon:"arrowR", cat:"Deals", desc:"Move associated deal",
    fields:[{ key:"stage", label:"To stage", type:"select", options:["Discovery","Qualification","Proposal","Negotiation","Closed Won","Closed Lost"] }] },
  { id:"add_tag", label:"Add tag", icon:"tag", cat:"Contacts", desc:"Tag the contact",
    fields:[{ key:"tag", label:"Tag", type:"text", placeholder:"hot-lead" }] },
  { id:"remove_tag", label:"Remove tag", icon:"tag", cat:"Contacts", desc:"Remove a tag",
    fields:[{ key:"tag", label:"Tag", type:"text", placeholder:"cold-lead" }] },
  { id:"notify_owner", label:"Notify owner", icon:"alert", cat:"Notifications", desc:"Internal notification",
    fields:[
      { key:"channel", label:"Via", type:"select", options:["In-app","Email","SMS","All channels"] },
      { key:"message", label:"Message", type:"textarea", placeholder:"{{contact_name}} needs attention" },
    ] },
  { id:"slack_message", label:"Slack message", icon:"send", cat:"Notifications", desc:"Post to Slack",
    fields:[
      { key:"channel", label:"Channel", type:"select", options:["#sales","#deals","#urgent","#wins","Direct to owner"] },
      { key:"message", label:"Message", type:"textarea", placeholder:"{{contact_name}}" },
    ] },
  { id:"ai_summarize", label:"AI: Summarize", icon:"ai", cat:"AI", desc:"AI summary of activity",
    fields:[
      { key:"source", label:"Source", type:"select", options:["Last call recording","Email thread","All recent activity","Deal history"] },
      { key:"save_to", label:"Save to", type:"select", options:["Contact notes","Deal notes","Activity log"] },
    ] },
  { id:"ai_research", label:"AI: Research", icon:"ai", cat:"AI", desc:"Auto-research contact",
    fields:[
      { key:"sources", label:"Sources", type:"select", options:["LinkedIn + company site","News + press","Everything available"] },
      { key:"save_to", label:"Save to", type:"select", options:["Contact notes","AI insights panel","Owner email briefing"] },
    ] },
  { id:"ai_draft", label:"AI: Draft email", icon:"ai", cat:"AI", desc:"AI writes personalized email",
    fields:[
      { key:"context", label:"Context", type:"textarea", placeholder:"Write a follow-up referencing our last call..." },
      { key:"tone", label:"Tone", type:"select", options:["Professional","Casual","Urgent","Friendly"] },
      { key:"save_as", label:"Then", type:"select", options:["Send immediately","Save as draft","Queue for approval"] },
    ] },
  { id:"enroll_workflow", label:"Enroll in workflow", icon:"workflow", cat:"Flow", desc:"Add to another workflow",
    fields:[{ key:"workflow", label:"Workflow", type:"text", placeholder:"Workflow name" }] },
  { id:"remove_workflow", label:"Remove from workflow", icon:"x", cat:"Flow", desc:"Unenroll from workflow",
    fields:[{ key:"workflow", label:"Workflow", type:"text", placeholder:"Workflow name" }] },
  { id:"webhook", label:"Webhook", icon:"link", cat:"Integrations", desc:"Send data to external URL",
    fields:[
      { key:"url", label:"URL", type:"text", placeholder:"https://..." },
      { key:"method", label:"Method", type:"select", options:["POST","PUT","GET"] },
    ] },
];

// ── DELAY & CONDITION OPTIONS ──
const DELAYS = [
  { id:"delay", label:"Wait / Delay", icon:"clock", cat:"Timing", desc:"Wait a specific duration",
    fields:[
      { key:"amount", label:"Wait for", type:"select", options:["5 minutes","15 minutes","30 minutes","1 hour","4 hours","1 day","2 days","3 days","5 days","1 week","2 weeks","1 month"] },
      { key:"business_hours", label:"Business hours only", type:"toggle" },
    ] },
  { id:"delay_until", label:"Wait until", icon:"calendar", cat:"Timing", desc:"Wait until a specific time",
    fields:[{ key:"type", label:"Wait until", type:"select", options:["Specific day/time","Next Monday 9am","Next business day","Contact timezone 9am"] }] },
];

const CONDITIONS = [
  { id:"if_else", label:"If / Then branch", icon:"filter", cat:"Logic", desc:"Split into Yes/No paths",
    fields:[
      { key:"field", label:"Check", type:"select", options:["Email opened","Email clicked","Email replied","Deal value","Contact score","Lead stage","Status","Has tag","Days since last activity","Deal stage","Task completed","Custom property"] },
      { key:"operator", label:"Operator", type:"select", options:["equals","not equals","greater than","less than","contains","is set","is not set"] },
      { key:"value", label:"Value", type:"text", placeholder:"Value..." },
    ] },
  { id:"split", label:"50/50 A/B split", icon:"filter", cat:"Logic", desc:"Randomly split into two paths",
    fields:[{ key:"split_pct", label:"Group A %", type:"select", options:["50%","60%","70%","80%","90%"] }] },
];

// ── TEMPLATES ──
const TEMPLATES = [
  { id:1, name:"New Lead Nurture", category:"Lead Management", uses:2340, desc:"Welcome new leads, score engagement, create follow-ups.",
    trigger:TRIGGERS[0],
    steps:[
      { id:"s1", action:"delay", label:"Wait / Delay", icon:"clock", config:{amount:"5 minutes"}, type:"delay" },
      { id:"s2", action:"send_email", label:"Send email", icon:"Em", config:{template:"Initial Outreach",subject:"Welcome {{first_name}}!"}, type:"action" },
      { id:"s3", action:"if_else", label:"If / Then branch", icon:"filter", config:{field:"Email opened",operator:"equals",value:"true"}, type:"condition",
        yes:[
          { id:"s4y", action:"add_score", label:"Adjust score", icon:"target", config:{operation:"+  Add points",points:"10"}, type:"action" },
          { id:"s5y", action:"create_task", label:"Create task", icon:"check", config:{title:"Call {{first_name}}",assignee:"Contact owner",priority:"High",due:"1 day"}, type:"action" },
        ],
        no:[
          { id:"s4n", action:"delay", label:"Wait / Delay", icon:"clock", config:{amount:"2 days"}, type:"delay" },
          { id:"s5n", action:"send_email", label:"Send email", icon:"Em", config:{template:"Follow-up",subject:"Still interested, {{first_name}}?"}, type:"action" },
        ],
      },
    ] },
  { id:2, name:"Deal Stage Automation", category:"Pipeline", uses:1870, desc:"Auto-create proposals and alerts when deals advance.",
    trigger:TRIGGERS.find(t=>t.id==="deal_stage"),
    steps:[
      { id:"s1", action:"send_email", label:"Send email", icon:"Em", config:{template:"Proposal",subject:"Your proposal"}, type:"action" },
      { id:"s2", action:"create_task", label:"Create task", icon:"check", config:{title:"Review proposal",priority:"High",due:"2 days"}, type:"action" },
      { id:"s3", action:"if_else", label:"If / Then branch", icon:"filter", config:{field:"Deal value",operator:"greater than",value:"50000"}, type:"condition",
        yes:[{ id:"s4y", action:"notify_owner", label:"Notify owner", icon:"alert", config:{channel:"All channels",message:"High-value deal!"}, type:"action" }],
        no:[],
      },
    ] },
  { id:3, name:"At-Risk Deal Recovery", category:"Pipeline", uses:980, desc:"Re-engage stagnant deals with outreach sequences.",
    trigger:TRIGGERS.find(t=>t.id==="deal_inactive"),
    steps:[
      { id:"s1", action:"add_tag", label:"Add tag", icon:"tag", config:{tag:"at-risk"}, type:"action" },
      { id:"s2", action:"send_email", label:"Send email", icon:"Em", config:{subject:"Checking in, {{first_name}}"}, type:"action" },
      { id:"s3", action:"delay", label:"Wait / Delay", icon:"clock", config:{amount:"3 days"}, type:"delay" },
      { id:"s4", action:"if_else", label:"If / Then branch", icon:"filter", config:{field:"Email replied",operator:"equals",value:"true"}, type:"condition",
        yes:[{ id:"s5y", action:"create_task", label:"Create task", icon:"check", config:{title:"Follow up reply",priority:"High",due:"1 day"}, type:"action" }],
        no:[
          { id:"s5n", action:"notify_owner", label:"Notify owner", icon:"alert", config:{message:"Deal at risk — no response"}, type:"action" },
          { id:"s6n", action:"change_stage", label:"Move deal stage", icon:"arrowR", config:{stage:"Closed Lost"}, type:"action" },
        ],
      },
    ] },
  { id:4, name:"Post-Call Follow-up", category:"Dialer", uses:3100, desc:"AI summarize calls, auto-score and follow up.",
    trigger:TRIGGERS.find(t=>t.id==="call_completed"),
    steps:[
      { id:"s1", action:"ai_summarize", label:"AI: Summarize", icon:"ai", config:{source:"Last call recording",save_to:"Contact notes"}, type:"action" },
      { id:"s2", action:"if_else", label:"If / Then branch", icon:"filter", config:{field:"Contact score",operator:"greater than",value:"connected"}, type:"condition",
        yes:[
          { id:"s3y", action:"add_score", label:"Adjust score", icon:"target", config:{operation:"+  Add points",points:"15"}, type:"action" },
          { id:"s4y", action:"send_email", label:"Send email", icon:"Em", config:{subject:"Great chatting, {{first_name}}"}, type:"action" },
        ],
        no:[{ id:"s3n", action:"create_task", label:"Create task", icon:"check", config:{title:"Retry call to {{first_name}}",due:"2 days"}, type:"action" }],
      },
    ] },
  { id:5, name:"Win Celebration & Onboard", category:"Pipeline", uses:1550, desc:"Celebrate wins and kick off onboarding.",
    trigger:TRIGGERS.find(t=>t.id==="deal_won"),
    steps:[
      { id:"s1", action:"slack_message", label:"Slack message", icon:"send", config:{channel:"#wins",message:"Deal WON!"}, type:"action" },
      { id:"s2", action:"remove_tag", label:"Remove tag", icon:"tag", config:{tag:"prospect"}, type:"action" },
      { id:"s3", action:"add_tag", label:"Add tag", icon:"tag", config:{tag:"customer"}, type:"action" },
      { id:"s4", action:"update_property", label:"Set property", icon:"edit", config:{property:"Status",value:"Customer"}, type:"action" },
      { id:"s5", action:"send_email", label:"Send email", icon:"Em", config:{subject:"Welcome aboard!"}, type:"action" },
      { id:"s6", action:"create_task", label:"Create task", icon:"check", config:{title:"Schedule onboarding",priority:"High",due:"1 day"}, type:"action" },
    ] },
  { id:6, name:"Lead Scoring Automation", category:"Lead Management", uses:2200, desc:"Auto-score leads based on engagement.",
    trigger:TRIGGERS.find(t=>t.id==="email_opened"),
    steps:[
      { id:"s1", action:"add_score", label:"Adjust score", icon:"target", config:{operation:"+  Add points",points:"5"}, type:"action" },
      { id:"s2", action:"if_else", label:"If / Then branch", icon:"filter", config:{field:"Contact score",operator:"greater than",value:"80"}, type:"condition",
        yes:[
          { id:"s3y", action:"update_property", label:"Set property", icon:"edit", config:{property:"Lead Stage",value:"Sales Qualified"}, type:"action" },
          { id:"s4y", action:"notify_owner", label:"Notify owner", icon:"alert", config:{channel:"In-app",message:"Hot lead! Score 80+"}, type:"action" },
        ],
        no:[],
      },
    ] },
];

/* ══════════════════════════════════════════════════
   VISUAL FLOW CANVAS
   ══════════════════════════════════════════════════ */
function uid() { return "s" + Math.random().toString(36).slice(2,9); }

function StepNode({ step, onEdit, onDelete, onAddAfter, readOnly }) {
  const isTrigger = step.type === "trigger";
  const isCond = step.type === "condition";
  const isDelay = step.type === "delay";
  const bg = isTrigger ? "#FAFAFA" : isCond ? "#FAFAFA" : isDelay ? "#8b8fa3" : "#FAFAFA";
  const ok = step.config && Object.values(step.config).some(v => v && v !== "");

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
      {/* Card */}
      <div onClick={() => !readOnly && onEdit(step)} style={{
        display:"flex", alignItems:"center", gap:12, padding:"14px 18px", width:320,
        background:T.card, border:"1.5px solid "+(ok?bg+"40":T.bdr),
        borderRadius: 8, cursor:readOnly?"default":"pointer", position:"relative", transition:"all 0.15s",
        boxShadow:ok?"0 2px 12px "+bg+"10":"none",
      }}>
        <div style={{ position:"absolute", left:0, top:10, bottom:10, width:3, borderRadius:"0 2px 2px 0", background:bg }}/>
        <div style={{
          width:34, height:34, borderRadius: 8, display:"flex", alignItems:"center", justifyContent:"center",
          background:bg+"12", border:"1px solid "+bg+"20", flexShrink:0,
        }}><Icon name={step.icon||"workflow"} size={15} color={bg}/></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:9, fontWeight:400, color:bg, letterSpacing:".05em", textTransform:"uppercase" }}>
              {isTrigger?"TRIGGER":isCond?"BRANCH":isDelay?"DELAY":"ACTION"}
            </span>
            {ok && <div style={{ width:5, height:5, borderRadius:3, background:"#FAFAFA" }}/>}
          </div>
          <div style={{ fontSize:12, fontWeight:400, color:T.txt, marginTop:2 }}>{step.label}</div>
          {ok && <div style={{ fontSize:9, color:T.tx3, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {Object.entries(step.config).filter(([,v])=>v&&typeof v==="string").slice(0,2).map(([,v])=>v.slice(0,28)).join(" · ")}
          </div>}
        </div>
        {!isTrigger && !readOnly && (
          <button onClick={e=>{e.stopPropagation();onDelete(step.id);}} style={{
            background:"none", border:"none", color:T.tx3, cursor:"pointer", opacity:0.4, padding:4 }}>
            <Icon name="x" size={12} color={T.tx3}/>
          </button>
        )}
      </div>

      {/* Branching */}
      {isCond && (
        <div style={{ display:"flex", gap:24, marginTop:0, position:"relative" }}>
          {/* Connector line between branches */}
          <div style={{ position:"absolute", top:0, left:"calc(50% - 72px)", width:"144px", height:16, borderLeft:"2px solid "+"rgba(250,250,250,0.3)", borderRight:"2px solid "+"rgba(250,250,250,0.3)", borderTop:"2px solid "+T.bdr }}/>
          {[["yes","#FAFAFA","YES"],["no","#FAFAFA","NO"]].map(([branch,color,label])=>(
            <div key={branch} style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:160 }}>
              <div style={{ width:2, height:16, background:color+"50" }}/>
              <div style={{ fontSize:9, fontWeight:400, color, background:color+"12", padding:"2px 10px", borderRadius: 8, marginBottom:6 }}>{label}</div>
              {(step[branch]||[]).map((child,i)=>(
                <div key={child.id}>
                  {i>0 && <div style={{ width:2, height:10, background:T.bdr, margin:"0 auto" }}/>}
                  <StepNode step={child} onEdit={onEdit} onDelete={onDelete} onAddAfter={onAddAfter} readOnly={readOnly}/>
                  {!readOnly && <div style={{ width:2, height:8, background:T.bdr, margin:"0 auto" }}/>}
                </div>
              ))}
              {!readOnly && (
                <button onClick={()=>onAddAfter(step.id,branch)} style={{
                  width:26, height:26, borderRadius:"50%", border:"2px dashed "+T.bdr,
                  background:T.card, color:T.tx3, display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer", fontSize:14, marginTop:2,
                }}>+</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Connector after non-condition */}
      {!isCond && !readOnly && (
        <>
          <div style={{ width:2, height:12, background:T.bdr }}/>
          <button onClick={()=>onAddAfter(step.id,"after")} style={{
            width:26, height:26, borderRadius:"50%", border:"2px dashed "+T.bdr,
            background:T.card, color:T.tx3, display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", fontSize:14,
          }}>+</button>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   STEP PICKER MODAL
   ══════════════════════════════════════════════════ */
function StepPicker({ onSelect, onClose, showTriggers }) {
  const [tab, setTab] = useState(showTriggers ? "triggers" : "actions");
  const [search, setSearch] = useState("");

  const items = tab==="triggers"?TRIGGERS:tab==="delays"?DELAYS:tab==="conditions"?CONDITIONS:ACTIONS;
  const filtered = search ? items.filter(i=>(i.label+" "+i.desc+" "+i.cat).toLowerCase().includes(search.toLowerCase())) : items;
  const cats = [...new Set(filtered.map(i=>i.cat))];

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ width:620, maxHeight:"80vh", ...crd, display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.bdr, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:15, fontWeight:400, color:T.txt }}>{showTriggers?"Choose Trigger":"Add Step"}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:T.tx3, cursor:"pointer" }}><Icon name="x" size={16} color={T.tx3}/></button>
        </div>

        {!showTriggers && (
          <div style={{ display:"flex", gap:0, padding:"0 20px", borderBottom:"1px solid "+T.bdr }}>
            {[{id:"actions",label:"Actions",n:ACTIONS.length},{id:"delays",label:"Timing",n:DELAYS.length},{id:"conditions",label:"Logic",n:CONDITIONS.length}].map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{
                padding:"10px 16px", border:"none", cursor:"pointer", background:"transparent",
                borderBottom:tab===t.id?"2px solid rgba(250,250,250,0.3)":"2px solid transparent",
                color:tab===t.id?T.txt:T.tx3, fontSize:11, fontWeight:400,
              }}>{t.label} <span style={{ fontSize:9, color:T.tx3 }}>({t.n})</span></button>
            ))}
          </div>
        )}

        <div style={{ padding:"12px 20px" }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..." style={{ ...inpS, background:T.sf }}/>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:"0 20px 16px" }}>
          {cats.map(cat=>(
            <div key={cat} style={{ marginBottom:14 }}>
              <div style={{ ...lbl, marginBottom:6 }}>{cat}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {filtered.filter(i=>i.cat===cat).map(item=>(
                  <button key={item.id} onClick={()=>onSelect(item)} style={{
                    display:"flex", alignItems:"flex-start", gap:10, padding:"10px 12px", borderRadius: 8,
                    border:"1px solid "+T.bdr, background:T.card, color:T.txt, fontSize:11, cursor:"pointer", textAlign:"left",
                  }}>
                    <Icon name={item.icon} size={14} color={item.cat==="AI"?"#FAFAFA":["Logic","Timing"].includes(item.cat)?"#FAFAFA":"#FAFAFA"}/>
                    <div>
                      <div style={{ fontWeight:400 }}>{item.label}</div>
                      {item.desc && <div style={{ fontSize:9, color:T.tx3, marginTop:2 }}>{item.desc}</div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   CONFIG MODAL
   ══════════════════════════════════════════════════ */
function ConfigModal({ step, fields, onSave, onClose }) {
  const [v, setV] = useState(step.config || {});
  const s = (k,val) => setV(p=>({...p,[k]:val}));
  const bg = step.type==="trigger"?"#FAFAFA":step.type==="condition"?"#FAFAFA":step.type==="delay"?"#8b8fa3":"#FAFAFA";

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ width:560, maxHeight:"85vh", ...crd, borderColor:bg+"30", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"16px 20px", borderBottom:"1px solid "+T.bdr, background:bg+"04" }}>
          <div style={{ width:36, height:36, borderRadius: 8, display:"flex", alignItems:"center", justifyContent:"center", background:bg+"12", border:"1px solid "+bg+"20" }}>
            <Icon name={step.icon||"workflow"} size={16} color={bg}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:9, fontWeight:400, color:bg, letterSpacing:".06em", textTransform:"uppercase" }}>Configure {step.type}</div>
            <div style={{ fontSize:15, fontWeight:400, color:T.txt }}>{step.label}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:T.tx3, cursor:"pointer" }}><Icon name="x" size={14} color={T.tx3}/></button>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          {(!fields||fields.length===0) ? (
            <div style={{ textAlign:"center", padding:30, color:T.tx3, fontSize:12 }}>Runs automatically — no configuration needed.</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {fields.map(f=>(
                <div key={f.key}>
                  <label style={{ fontSize:11, fontWeight:400, color:T.tx2, display:"block", marginBottom:5 }}>{f.label}</label>
                  {f.type==="select" ? (
                    <Select value={v[f.key]||""} onChange={e=>s(f.key,e.target.value)} placeholder="— Select —" options={f.options}/>
                  ) : f.type==="email_body" ? (
                    <div>
                      <div style={{ display:"flex", gap:2, padding:"6px 8px", background:T.sf, borderRadius:"8px 8px 0 0", border:"1px solid "+T.bdr, borderBottom:"none" }}>
                        {["{{first_name}}","{{company}}","{{deal_name}}"].map(m=>(
                          <button key={m} onClick={()=>s(f.key,(v[f.key]||"")+" "+m)} style={{
                            padding:"2px 7px", borderRadius:4, border:"1px solid "+T.bdr, background:T.card,
                            color:"#FAFAFA", fontSize:9, cursor:"pointer", fontFamily:"inherit" }}>{m}</button>
                        ))}
                      </div>
                      <textarea value={v[f.key]||""} onChange={e=>s(f.key,e.target.value)} placeholder={f.placeholder}
                        style={{ ...inpS, height:140, resize:"vertical", lineHeight:1.7, borderRadius:"0 0 8px 8px", borderTop:"none" }}/>
                    </div>
                  ) : f.type==="textarea" ? (
                    <textarea value={v[f.key]||""} onChange={e=>s(f.key,e.target.value)} placeholder={f.placeholder} rows={3}
                      style={{ ...inpS, resize:"vertical", lineHeight:1.6 }}/>
                  ) : f.type==="toggle" ? (
                    <button onClick={()=>s(f.key,!v[f.key])} style={{
                      display:"inline-flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:6, cursor:"pointer",
                      background:v[f.key]?"rgba(250,250,250,0.06)":T.sf, border:"1px solid "+(v[f.key]?"rgba(250,250,250,0.18)":T.bdr),
                      color:v[f.key]?"#FAFAFA":T.tx3, fontSize:11, fontWeight:400,
                    }}>
                      <div style={{ width:32, height:16, borderRadius: 8, background:v[f.key]?"#FAFAFA":T.bdr, position:"relative" }}>
                        <div style={{ width:12, height:12, borderRadius:6, background:"rgba(255,255,255,0.06)", position:"absolute", top:2, left:v[f.key]?18:2, transition:"left 0.2s" }}/>
                      </div>
                      {v[f.key]?"Enabled":"Disabled"}
                    </button>
                  ) : (
                    <input value={v[f.key]||""} onChange={e=>s(f.key,e.target.value)} placeholder={f.placeholder} style={inpS}/>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, padding:"14px 20px", borderTop:"1px solid "+T.bdr, background:T.sf }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={()=>{onSave(v);onClose();}}>Save</Btn>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ══════════════════════════════════════════════════ */
export default function WorkflowsPage({ isMobile }) {
  // Persist sub-tab in URL hash
  const getInitialView = () => {
    const hash = window.location.hash.replace("#", "");
    if (["active", "campaigns", "templates", "builder"].includes(hash)) return hash;
    return "active";
  };
  const [view, setView] = useState(getInitialView);
  const changeView = (v) => {
    setView(v);
    window.location.hash = v === "active" ? "" : v;
  };
  const [sel, setSel] = useState(null);
  const toast = useToast();

  // Builder state
  const [trigger, setTrigger] = useState(null);
  const [steps, setSteps] = useState([]);
  const [wfName, setWfName] = useState("");
  const [goalExit, setGoalExit] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Modals
  const [showPicker, setShowPicker] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [testingWf, setTestingWf] = useState(null);
  const [logsWf, setLogsWf] = useState(null);

  // DB workflows
  const [myWorkflows, setMyWorkflows] = useState([]);
  const [wfLoaded, setWfLoaded] = useState(false);

  const loadWorkflows = useCallback(async () => {
    try {
      const { data, error } = await supabase.from("workflows").select("*").eq("org_id", window.__coreflow_org_id).order("created_at",{ascending:false}).limit(50);
      if (error) throw error;
      if (data) setMyWorkflows(data);
    } catch(e) {
      console.error("[WorkflowsPage] Failed to load workflows:", e);
      toast?.error?.("Failed to load workflows: " + (e.message || "unknown error"));
    }
    setWfLoaded(true);
  },[toast]);

  useEffect(()=>{loadWorkflows();},[loadWorkflows]);

  // ── Tree manipulation helpers ──
  const findAndUpdate = (nodes, targetId, updater) =>
    nodes.map(n => {
      if (n.id===targetId) return updater(n);
      const r = {...n};
      if (n.yes) r.yes = findAndUpdate(n.yes, targetId, updater);
      if (n.no) r.no = findAndUpdate(n.no, targetId, updater);
      return r;
    });

  const findAndRemove = (nodes, targetId) =>
    nodes.filter(n=>n.id!==targetId).map(n=>{
      const r = {...n};
      if (n.yes) r.yes = findAndRemove(n.yes, targetId);
      if (n.no) r.no = findAndRemove(n.no, targetId);
      return r;
    });

  const addAfterInList = (nodes, parentId, newStep) => {
    const result = [];
    for (const n of nodes) {
      const updated = {...n};
      if (n.yes) updated.yes = addAfterInList(n.yes, parentId, newStep);
      if (n.no) updated.no = addAfterInList(n.no, parentId, newStep);
      result.push(updated);
      if (n.id===parentId) result.push(newStep);
    }
    return result;
  };

  const handlePickerSelect = (item) => {
    if (showPicker==="trigger") {
      setTrigger({id:uid(),action:item.id,label:item.label,icon:item.icon,type:"trigger",config:{},fields:item.fields});
      setShowPicker(null);
      return;
    }

    const isCond = item.id==="if_else"||item.id==="split";
    const isDel = item.id==="delay"||item.id==="delay_until";
    const newStep = {
      id:uid(), action:item.id, label:item.label, icon:item.icon,
      type:isCond?"condition":isDel?"delay":"action",
      config:{}, ...(isCond?{yes:[],no:[]}:{}),
    };

    if (typeof showPicker==="object" && showPicker.parentId) {
      if (showPicker.branch==="after") {
        // Check if parent is the trigger (not in steps array)
        const foundInSteps = JSON.stringify(steps).includes('"'+showPicker.parentId+'"');
        if (foundInSteps) {
          setSteps(prev=>addAfterInList(prev, showPicker.parentId, newStep));
        } else {
          // Parent is the trigger — prepend to steps
          setSteps(prev=>[newStep, ...prev]);
        }
      } else {
        // Add to yes/no branch
        setSteps(prev=>findAndUpdate(prev, showPicker.parentId, n=>({
          ...n, [showPicker.branch]:[...(n[showPicker.branch]||[]), newStep],
        })));
      }
    } else {
      setSteps(p=>[...p, newStep]);
    }
    setShowPicker(null);
  };

  const handleEditStep = (step) => {
    const allItems = [...TRIGGERS,...ACTIONS,...DELAYS,...CONDITIONS];
    const def = allItems.find(i=>i.id===step.action);
    setEditingStep({...step, fields:def?.fields||step.fields||[]});
  };

  const handleSaveConfig = (config) => {
    if (editingStep.type==="trigger") {
      setTrigger(p=>({...p, config}));
    } else {
      setSteps(prev=>findAndUpdate(prev, editingStep.id, n=>({...n, config})));
    }
    setEditingStep(null);
  };

  const countSteps = (nodes) => nodes.reduce((s,n)=>s+1+countSteps(n.yes||[])+countSteps(n.no||[]),0);
  const totalSteps = countSteps(steps)+(trigger?1:0);

  // ── Save workflow ──
  const saveWorkflow = async (status) => {
    if (!wfName.trim()) { toast?.error("Give your workflow a name"); return; }
    if (!trigger) { toast?.error("Add a trigger first"); return; }
    setSaving(true);
    try {
      const payload = {
        org_id: window.__coreflow_org_id,
        name: wfName,
        trigger_type: trigger.action,
        trigger_config: trigger.config,
        steps, goal_exit:goalExit||null, status,
        created_by: window.__coreflow_user_id||null,
      };
      if (editingId) {
        const { error } = await supabase.from("workflows").update(payload).eq("id",editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("workflows").insert(payload);
        if (error) throw error;
      }
      toast?.success(status==="active"?"Workflow activated!":"Draft saved");
      setSteps([]); setTrigger(null); setWfName(""); setGoalExit(""); setEditingId(null);
      loadWorkflows(); changeView("active");
    } catch(e) { toast?.error("Failed to save: "+e.message); }
    setSaving(false);
  };

  const editWorkflow = (wf) => {
    setEditingId(wf.id); setWfName(wf.name); setGoalExit(wf.goal_exit||"");
    setSteps(wf.steps||[]);
    const tDef = TRIGGERS.find(t=>t.id===wf.trigger_type);
    if (tDef) setTrigger({id:uid(),action:wf.trigger_type,label:tDef.label,icon:tDef.icon,type:"trigger",config:wf.trigger_config||{},fields:tDef.fields});
    changeView("builder");
  };

  const toggleStatus = async (wf) => {
    const ns = wf.status==="active"?"paused":"active";
    await supabase.from("workflows").update({status:ns}).eq("id",wf.id);
    toast?.success(ns==="active"?"Workflow activated":"Workflow paused");
    loadWorkflows();
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm("Delete this workflow?")) return;
    await supabase.from("workflows").delete().eq("id",id);
    toast?.success("Workflow deleted"); loadWorkflows();
  };

  const loadTemplate = (tpl) => {
    setTrigger(tpl.trigger?{id:uid(),action:tpl.trigger.id,label:tpl.trigger.label,icon:tpl.trigger.icon,type:"trigger",config:{},fields:tpl.trigger.fields}:null);
    setSteps(tpl.steps||[]); setWfName(tpl.name); setEditingId(null); changeView("builder");
  };

  return (
    <div style={{ padding:18 }}>
      {/* Tab bar */}
      <div style={{ display:"flex", gap:1, marginBottom:18, borderBottom:"1px solid "+T.bdr }}>
        {[
          {id:"active",label:"My Workflows",count:myWorkflows.length},
          {id:"campaigns",label:"Email Campaigns"},
          {id:"templates",label:"Templates",count:TEMPLATES.length},
          {id:"builder",label:"Build Custom"},
        ].map(t=>(
          <button key={t.id} onClick={()=>{changeView(t.id);setSel(null);}} style={{
            padding:"10px 20px", border:"none", cursor:"pointer", background:"transparent",
            borderBottom:view===t.id?"2px solid rgba(250,250,250,0.3)":"2px solid transparent",
            color:view===t.id?T.txt:T.tx3, fontSize:12, fontWeight:400, display:"flex", alignItems:"center", gap:6,
          }}>
            {t.label}
            {t.count!=null && <span style={{ padding:"1px 6px", borderRadius: 8, background:view===t.id?"rgba(250,250,250,0.08)":T.sf, color:view===t.id?"#FAFAFA":T.tx3, fontSize:10, fontWeight:400 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ── CAMPAIGNS ── */}
      {view==="campaigns" && <CampaignsPanel />}

      {/* ── MY WORKFLOWS ── */}
      {view==="active" && <div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
          {[
            {l:"Active",v:myWorkflows.filter(w=>w.status==="active").length,c:"#FAFAFA"},
            {l:"Paused",v:myWorkflows.filter(w=>w.status==="paused").length,c:"#FAFAFA"},
            {l:"Drafts",v:myWorkflows.filter(w=>w.status==="draft").length,c:"#8b8fa3"},
            {l:"Total",v:myWorkflows.length,c:"#FAFAFA"},
          ].map(st=>(
            <div key={st.l} style={{ ...crd, padding:"16px 18px" }}>
              <div style={{ fontSize:10, color:T.tx3, fontWeight:400, marginBottom:6 }}>{st.l}</div>
              <div style={{ fontSize:26, fontWeight:400, color:st.c, lineHeight:1 }}>{st.v}</div>
            </div>
          ))}
        </div>

        {myWorkflows.length===0 && wfLoaded ? (
          <div style={{ ...crd, textAlign:"center", padding:"48px 24px" }}>
            <div style={{ width:48, height:48, borderRadius: 8, background:"rgba(250,250,250,0.04)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
              <Icon name="workflow" size={24} color={"#FAFAFA"}/>
            </div>
            <div style={{ fontSize:15, fontWeight:400, color:T.txt, marginBottom:4 }}>No workflows yet</div>
            <div style={{ fontSize:12, color:T.tx3, marginBottom:16 }}>Start from a template or build your own.</div>
            <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
              <Btn onClick={()=>changeView("templates")}>Browse Templates</Btn>
              <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={()=>changeView("builder")}>Build Custom</Btn>
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {myWorkflows.map(w=>{
              const tDef = TRIGGERS.find(t=>t.id===w.trigger_type);
              const stepCount = countSteps(w.steps||[]);
              return (
                <div key={w.id} style={{ ...crd, padding:"16px 20px", display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{
                    width:10, height:10, borderRadius:5, flexShrink:0,
                    background:w.status==="active"?"#FAFAFA":w.status==="draft"?"#FAFAFA":T.tx3,
                    boxShadow:w.status==="active"?"0 0 8px "+"rgba(250,250,250,0.25)":"none",
                  }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:400, color:T.txt }}>{w.name}</div>
                    <div style={{ fontSize:10, color:T.tx3, marginTop:3 }}>
                      {tDef?<><Icon name={tDef.icon} size={10} color={"#FAFAFA"}/> {tDef.label}</>:w.trigger_type||"No trigger"}
                      {" → "}{stepCount} step{stepCount!==1?"s":""} · {(() => { const _d = new Date(w.created_at); return String(_d.getMonth()+1).padStart(2,"0")+"/"+String(_d.getDate()).padStart(2,"0")+"/"+String(_d.getFullYear()).slice(-2); })()}
                    </div>
                  </div>
                  <Badge color={w.status==="active"?"#FAFAFA":w.status==="draft"?"#FAFAFA":T.tx3}>{w.status}</Badge>
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn style={{ fontSize:10, color:"#FAFAFA" }} onClick={()=>setTestingWf(w)}>Test</Btn>
                    <Btn style={{ fontSize:10 }} onClick={()=>setLogsWf(w)}>Logs</Btn>
                    <Btn style={{ fontSize:10 }} onClick={()=>toggleStatus(w)}>{w.status==="active"?"Pause":"Activate"}</Btn>
                    <Btn style={{ fontSize:10 }} onClick={()=>editWorkflow(w)}>Edit</Btn>
                    <Btn style={{ fontSize:10, color:"#FAFAFA" }} onClick={()=>deleteWorkflow(w.id)}>Delete</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* ── TEMPLATES ── */}
      {view==="templates" && !sel && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {TEMPLATES.map(t=>(
            <div key={t.id} style={{ ...crd, padding:20, cursor:"pointer" }} onClick={()=>setSel(t)}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                <Badge color={"rgba(250,250,250,0.3)"}>{t.category}</Badge>
                <span style={{ fontSize:9, color:T.tx3 }}>{t.uses.toLocaleString()} users</span>
              </div>
              <div style={{ fontSize:15, fontWeight:400, color:T.txt, marginBottom:4 }}>{t.name}</div>
              <div style={{ fontSize:11, color:T.tx3, lineHeight:1.5, marginBottom:14 }}>{t.desc}</div>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:12 }}>
                {t.trigger && <span style={{ fontSize:9, padding:"2px 8px", borderRadius:6, background:"rgba(250,250,250,0.06)", color:"#FAFAFA", fontWeight:400, display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="zap" size={9} color="#FAFAFA" /> {t.trigger.label}</span>}
                <span style={{ fontSize:9, padding:"2px 8px", borderRadius:6, background:"rgba(250,250,250,0.06)", color:"#FAFAFA", fontWeight:400 }}>{t.steps.length} steps</span>
                {t.steps.some(s=>s.type==="condition") && <span style={{ fontSize:9, padding:"2px 8px", borderRadius:6, background:"rgba(250,250,250,0.06)", color:"#FAFAFA", fontWeight:400 }}>Branching</span>}
              </div>
              <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={e=>{e.stopPropagation();loadTemplate(t);}} style={{ width:"100%" }}>Use Template →</Btn>
            </div>
          ))}
        </div>
      )}

      {view==="templates" && sel && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999 }}>
          <div style={{ width:700, maxHeight:"85vh", ...crd, overflow:"auto", padding:24, boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
              <div>
                <Badge color={"rgba(250,250,250,0.3)"}>{sel.category}</Badge>
                <div style={{ fontSize:22, fontWeight:400, color:T.txt, marginTop:4 }}>{sel.name}</div>
                <div style={{ fontSize:12, color:T.tx3, marginTop:4 }}>{sel.desc}</div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={()=>{loadTemplate(sel);setSel(null);}}>Use Template</Btn>
                <button onClick={()=>setSel(null)} style={{ background:"none", border:"none", color:T.tx3, cursor:"pointer" }}><Icon name="x" size={16} color={T.tx3}/></button>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:20 }}>
              {sel.trigger && <StepNode step={{...sel.trigger,type:"trigger",config:{}}} onEdit={()=>{}} onDelete={()=>{}} onAddAfter={()=>{}} readOnly/>}
              {sel.trigger && <div style={{ width:2, height:12, background:T.bdr }}/>}
              {sel.steps.map((s,i)=>(
                <div key={s.id||i}>
                  {i>0 && <div style={{ width:2, height:10, background:T.bdr, margin:"0 auto" }}/>}
                  <StepNode step={s} onEdit={()=>{}} onDelete={()=>{}} onAddAfter={()=>{}} readOnly/>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── BUILDER ── */}
      {view==="builder" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 260px", gap:14 }}>
          <div>
            {/* Toolbar */}
            <div style={{ ...crd, padding:"12px 20px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <input value={wfName} onChange={e=>setWfName(e.target.value)} placeholder="Workflow name..." style={{
                background:"transparent", border:"none", outline:"none", fontSize:16, fontWeight:400, color:T.txt, width:280 }}/>
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:10, color:T.tx3, marginRight:8 }}>{totalSteps} steps</span>
                <Btn onClick={()=>saveWorkflow("draft")} disabled={saving}>{saving?"Saving...":"Save Draft"}</Btn>
                <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={()=>saveWorkflow("active")} disabled={saving||totalSteps<2}>{saving?"Saving...":"Activate"}</Btn>
              </div>
            </div>

            {/* Canvas */}
            <div style={{ ...crd, padding:24, minHeight:500, overflow:"auto" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                {trigger ? (
                  <StepNode step={trigger} onEdit={handleEditStep} onDelete={()=>setTrigger(null)} onAddAfter={(id,branch)=>setShowPicker({parentId:id,branch})}/>
                ) : (
                  <div style={{ textAlign:"center", padding:"40px 0" }}>
                    <div style={{ width:56, height:56, borderRadius: 8, background:"rgba(250,250,250,0.04)", border:"1px solid "+"rgba(250,250,250,0.08)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
                      <Icon name="workflow" size={28} color={"#FAFAFA"}/>
                    </div>
                    <div style={{ fontSize:15, fontWeight:400, color:T.txt, marginBottom:4 }}>Start with a trigger</div>
                    <div style={{ fontSize:12, color:T.tx3, marginBottom:16 }}>What event should start this workflow?</div>
                    <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={()=>setShowPicker("trigger")}>+ Choose Trigger</Btn>
                  </div>
                )}

                {trigger && steps.map((step,i)=>(
                  <div key={step.id}>
                    {i===0 && <div style={{ width:2, height:10, background:T.bdr, margin:"0 auto" }}/>}
                    <StepNode step={step} onEdit={handleEditStep} onDelete={id=>setSteps(prev=>findAndRemove(prev,id))} onAddAfter={(id,branch)=>setShowPicker({parentId:id,branch})}/>
                  </div>
                ))}

                {trigger && steps.length===0 && (
                  <>
                    <div style={{ width:2, height:16, background:T.bdr }}/>
                    <button onClick={()=>setShowPicker({parentId:null,branch:"root"})} style={{
                      padding:"12px 24px", borderRadius: 8, border:"2px dashed "+T.bdr,
                      background:"transparent", color:T.tx3, fontSize:12, fontWeight:400, cursor:"pointer", width:320,
                    }}>+ Add First Step</button>
                  </>
                )}
              </div>
            </div>

            {/* Goal-based exit */}
            {trigger && (
              <div style={{ ...crd, padding:"14px 20px", marginTop:10, display:"flex", alignItems:"center", gap:10 }}>
                <Icon name="star" size={14} color={"#FAFAFA"}/>
                <span style={{ fontSize:11, fontWeight:400, color:T.tx2 }}>Goal (exit when):</span>
                <Select value={goalExit} onChange={e=>setGoalExit(e.target.value)} placeholder="No goal — run all steps" style={{ flex:1 }}
                  options={[{value:"",label:"No goal — run all steps"},{value:"deal_won",label:"Deal is won"},{value:"deal_lost",label:"Deal is lost"},{value:"email_replied",label:"Contact replies to email"},{value:"meeting_booked",label:"Meeting is booked"},{value:"score_above_80",label:"Score reaches 80+"},{value:"stage_closed",label:"Deal reaches Closed stage"}]} />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ ...crd, padding:14 }}>
              <div style={lbl}>Quick Add</div>
              {[...ACTIONS.slice(0,6),...DELAYS,...CONDITIONS].map(a=>(
                <button key={a.id} onClick={()=>{
                  if (!trigger){ toast?.error("Add a trigger first"); return; }
                  const isCond = a.id==="if_else"||a.id==="split";
                  const isDel = a.id==="delay"||a.id==="delay_until";
                  setSteps(p=>[...p,{
                    id:uid(), action:a.id, label:a.label, icon:a.icon,
                    type:isCond?"condition":isDel?"delay":"action",
                    config:{}, ...(isCond?{yes:[],no:[]}:{}),
                  }]);
                }} style={{
                  display:"flex", alignItems:"center", gap:8, padding:"7px 8px", borderRadius:6,
                  border:"none", background:"transparent", color:T.tx2, fontSize:11,
                  cursor:"pointer", width:"100%", textAlign:"left",
                }}>
                  <Icon name={a.icon} size={13} color={a.cat==="AI"?"#FAFAFA":["Logic","Timing"].includes(a.cat)?"#FAFAFA":"#FAFAFA"}/>
                  <span style={{ flex:1, fontWeight:400 }}>{a.label}</span>
                  <span style={{ fontSize:9, color:T.tx3 }}>{a.cat}</span>
                </button>
              ))}
              <button onClick={()=>setShowPicker({parentId:null,branch:"root"})} style={{
                display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:6,
                border:"1px dashed "+T.bdr, background:"transparent", color:"#FAFAFA", fontSize:11,
                cursor:"pointer", width:"100%", textAlign:"left", marginTop:6, fontWeight:400,
              }}>Browse all steps →</button>
            </div>
            {trigger && (
              <div style={{ ...crd, padding:14, borderColor:"rgba(250,250,250,0.12)", background:"rgba(250,250,250,0.02)" }}>
                <div style={{ fontSize:9, fontWeight:400, color:"#FAFAFA", letterSpacing:".06em", marginBottom:8 }}>WORKFLOW SUMMARY</div>
                {[
                  ["Trigger",trigger.label],
                  ["Steps",totalSteps],
                  ["Branches",steps.filter(s=>s.type==="condition").length],
                  ["Goal",goalExit||"None"],
                ].map(([l,v])=>(
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:10 }}>
                    <span style={{ color:T.tx3 }}>{l}</span>
                    <span style={{ color:T.txt, fontWeight:400 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODALS ── */}
      {showPicker && <StepPicker showTriggers={showPicker==="trigger"} onSelect={handlePickerSelect} onClose={()=>setShowPicker(null)}/>}
      {editingStep && <ConfigModal step={editingStep} fields={editingStep.fields||[]} onSave={handleSaveConfig} onClose={()=>setEditingStep(null)}/>}
      {testingWf && <TestRunner workflow={testingWf} onClose={()=>setTestingWf(null)} toast={toast}/>}
      {logsWf && <LogViewer workflow={logsWf} onClose={()=>setLogsWf(null)}/>}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   TEST RUNNER — Simulate a workflow step-by-step
   ══════════════════════════════════════════════════ */
function TestRunner({ workflow, onClose, toast }) {
  const store = useStore();
  const contacts = store.contacts || [];
  const [selectedContact, setSelectedContact] = useState(null);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]); // [{stepId, label, action, status, message, timestamp}]
  const [done, setDone] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [overallStatus, setOverallStatus] = useState(null); // "passed" | "failed"

  const allItems = [...TRIGGERS,...ACTIONS,...DELAYS,...CONDITIONS];

  // Validate a single step's config
  const validateStep = (step) => {
    const def = allItems.find(i => i.id === step.action);
    const fields = def?.fields || [];
    const cfg = step.config || {};
    const errors = [];

    // Check required fields (any field without a value)
    for (const f of fields) {
      if (f.type === "toggle") continue; // toggles are always valid
      if (!cfg[f.key] && f.options?.length > 0) {
        errors.push(`"${f.label}" not configured`);
      }
    }

    // Action-specific validation
    if (step.action === "send_email") {
      if (!cfg.subject) errors.push("Missing email subject");
      if (!cfg.body && !cfg.template) errors.push("Missing email body or template");
    }
    if (step.action === "send_sms" && !cfg.message) errors.push("Missing SMS message");
    if (step.action === "create_task" && !cfg.title) errors.push("Missing task title");
    if (step.action === "add_tag" && !cfg.tag) errors.push("Missing tag name");
    if (step.action === "remove_tag" && !cfg.tag) errors.push("Missing tag name");
    if (step.action === "webhook" && !cfg.url) errors.push("Missing webhook URL");
    if (step.action === "change_stage" && !cfg.stage) errors.push("Missing target stage");
    if (step.action === "add_score" && !cfg.points) errors.push("Missing point value");
    if (step.action === "update_property" && !cfg.property) errors.push("Missing property name");
    if (step.action === "delay" && !cfg.amount) errors.push("Missing delay duration");
    if (step.action === "if_else" && !cfg.field) errors.push("Missing condition field");

    return errors;
  };

  // Recursively test all steps in the tree
  const testSteps = async (stepsList, depth = 0) => {
    const log = [];
    for (const step of stepsList) {
      // Simulate delay
      await new Promise(r => setTimeout(r, 400 + Math.random() * 300));

      const errors = validateStep(step);
      const passed = errors.length === 0;
      const entry = {
        stepId: step.id,
        label: step.label,
        action: step.action,
        type: step.type,
        status: passed ? "passed" : "failed",
        message: passed ? getSuccessMessage(step) : errors.join("; "),
        timestamp: new Date().toISOString(),
        depth,
      };
      log.push(entry);
      setResults(prev => [...prev, entry]);

      if (!passed) continue; // skip children on failure

      // Test branches for conditions
      if (step.type === "condition" && step.yes?.length > 0) {
        const yesEntry = { stepId: step.id + "_yes", label: "YES branch", action: "branch", type: "branch", status: "info", message: `Testing ${step.yes.length} step(s)`, timestamp: new Date().toISOString(), depth: depth + 1 };
        log.push(yesEntry);
        setResults(prev => [...prev, yesEntry]);
        const yesLog = await testSteps(step.yes, depth + 1);
        log.push(...yesLog);
      }
      if (step.type === "condition" && step.no?.length > 0) {
        const noEntry = { stepId: step.id + "_no", label: "NO branch", action: "branch", type: "branch", status: "info", message: `Testing ${step.no.length} step(s)`, timestamp: new Date().toISOString(), depth: depth + 1 };
        log.push(noEntry);
        setResults(prev => [...prev, noEntry]);
        const noLog = await testSteps(step.no, depth + 1);
        log.push(...noLog);
      }
    }
    return log;
  };

  const getSuccessMessage = (step) => {
    const c = selectedContact;
    const name = c ? (c.fn || c.first_name || "") + " " + (c.ln || c.last_name || "") : "Test Contact";
    switch (step.action) {
      case "send_email": return `Would send "${step.config?.subject || "email"}" to ${c?.email || "contact"}`;
      case "send_sms": return `Would send SMS to ${c?.phone || "contact's phone"}`;
      case "create_task": return `Would create task: "${step.config?.title || "task"}"`;
      case "add_tag": return `Would add tag: "${step.config?.tag}"`;
      case "remove_tag": return `Would remove tag: "${step.config?.tag}"`;
      case "add_score": return `Would ${step.config?.operation || "add"} ${step.config?.points || "?"} points`;
      case "update_property": return `Would set ${step.config?.property} = "${step.config?.value}"`;
      case "change_stage": return `Would move deal to "${step.config?.stage}"`;
      case "notify_owner": return `Would notify owner via ${step.config?.channel || "in-app"}`;
      case "slack_message": return `Would post to ${step.config?.channel || "Slack"}`;
      case "ai_summarize": return `Would summarize ${step.config?.source || "activity"}`;
      case "ai_research": return `Would research from ${step.config?.sources || "all sources"}`;
      case "ai_draft": return `Would draft email with ${step.config?.tone || "professional"} tone`;
      case "delay": return `Would wait ${step.config?.amount || "?"}`;
      case "delay_until": return `Would wait until ${step.config?.type || "?"}`;
      case "if_else": return `Would check: ${step.config?.field} ${step.config?.operator} ${step.config?.value || ""}`;
      case "split": return `Would split ${step.config?.split_pct || "50%"} / ${100 - parseInt(step.config?.split_pct) || 50}%`;
      case "webhook": return `Would call ${step.config?.method || "POST"} ${step.config?.url || "URL"}`;
      case "enroll_workflow": return `Would enroll in "${step.config?.workflow}"`;
      default: return "Step validated";
    }
  };

  const runTest = async () => {
    setRunning(true);
    setResults([]);
    setDone(false);
    setOverallStatus(null);

    // Test trigger first
    const triggerDef = TRIGGERS.find(t => t.id === workflow.trigger_type);
    const triggerEntry = {
      stepId: "trigger", label: triggerDef?.label || workflow.trigger_type || "Trigger",
      action: workflow.trigger_type, type: "trigger", status: "passed",
      message: `Trigger: ${triggerDef?.label || "configured"}`, timestamp: new Date().toISOString(), depth: 0,
    };
    setResults([triggerEntry]);
    await new Promise(r => setTimeout(r, 500));

    // Test all steps
    const allLogs = [triggerEntry, ...(await testSteps(workflow.steps || []))];
    const hasFailed = allLogs.some(l => l.status === "failed");
    setOverallStatus(hasFailed ? "failed" : "passed");
    setDone(true);
    setRunning(false);

    // Save log to DB
    try {
      await supabase.from("workflow_logs").insert({
        org_id: window.__coreflow_org_id,
        workflow_id: workflow.id,
        contact_id: selectedContact?.id || null,
        contact_name: selectedContact ? [selectedContact.fn || selectedContact.first_name, selectedContact.ln || selectedContact.last_name].filter(Boolean).join(" ") : "Test (no contact)",
        status: hasFailed ? "test_failed" : "test_passed",
        is_test: true,
        steps_executed: allLogs,
        error_message: hasFailed ? allLogs.filter(l => l.status === "failed").map(l => l.label + ": " + l.message).join(" | ") : null,
        error_step: hasFailed ? allLogs.find(l => l.status === "failed")?.label : null,
        completed_at: new Date().toISOString(),
        created_by: window.__coreflow_user_id || null,
      });
    } catch (e) { console.warn("Log save failed:", e.message); }
  };

  const filtered = contacts.filter(c => !searchQ || [c.fn,c.ln,c.first_name,c.last_name,c.email,c.co,c.company].join(" ").toLowerCase().includes(searchQ.toLowerCase()));

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ width:680, maxHeight:"85vh", ...crd, display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.bdr, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:9, fontWeight:400, color:"#FAFAFA", letterSpacing:".06em" }}>TEST WORKFLOW</div>
            <div style={{ fontSize:16, fontWeight:400, color:T.txt }}>{workflow.name}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:T.tx3, cursor:"pointer" }}><Icon name="x" size={16} color={T.tx3}/></button>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          {/* Contact picker */}
          {!running && !done && (
            <div style={{ marginBottom:20 }}>
              <div style={lbl}>Select a contact to test with (optional)</div>
              <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search contacts..." style={{ ...inpS, marginBottom:8 }}/>
              <div style={{ maxHeight:160, overflow:"auto", border:"1px solid "+T.bdr, borderRadius:8 }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", cursor:"pointer", fontSize:11, borderBottom:"1px solid "+T.bdr+"50" }}>
                  <input type="radio" name="testContact" checked={!selectedContact} onChange={()=>setSelectedContact(null)} style={{ accentColor:"#FAFAFA" }}/>
                  <span style={{ fontWeight:400, color:T.tx3 }}>No contact — test config only</span>
                </label>
                {filtered.slice(0,50).map(c=>(
                  <label key={c.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", cursor:"pointer", fontSize:11, borderBottom:"1px solid "+T.bdr+"30" }}>
                    <input type="radio" name="testContact" checked={selectedContact?.id===c.id} onChange={()=>setSelectedContact(c)} style={{ accentColor:"#FAFAFA" }}/>
                    <span style={{ fontWeight:400, color:T.txt }}>{c.fn||c.first_name} {c.ln||c.last_name}</span>
                    <span style={{ color:T.tx3 }}>{c.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {(running || done) && (
            <div>
              {results.map((r, i) => (
                <div key={i} style={{
                  display:"flex", alignItems:"flex-start", gap:10, padding:"10px 12px", marginBottom:4,
                  marginLeft: (r.depth || 0) * 24,
                  borderRadius:8, background:r.status==="failed"?"rgba(250,250,250,0.04)":r.status==="info"?"rgba(250,250,250,0.03)":"transparent",
                  border:"1px solid "+(r.status==="failed"?"rgba(250,250,250,0.12)":r.status==="info"?"rgba(250,250,250,0.08)":"transparent"),
                  animation: "fadeIn 0.3s ease",
                }}>
                  <div style={{
                    width:20, height:20, borderRadius: 8, display:"flex", alignItems:"center", justifyContent:"center",
                    background:r.status==="passed"?"rgba(250,250,250,0.08)":r.status==="failed"?"rgba(250,250,250,0.08)":r.status==="info"?"rgba(250,250,250,0.08)":"rgba(250,250,250,0.08)",
                    border:"1px solid "+(r.status==="passed"?"rgba(250,250,250,0.18)":r.status==="failed"?"rgba(250,250,250,0.18)":r.status==="info"?"rgba(250,250,250,0.18)":"rgba(250,250,250,0.18)"),
                    flexShrink:0, marginTop:1,
                  }}>
                    <span style={{ fontSize:10, color:r.status==="passed"?"#FAFAFA":r.status==="failed"?"#FAFAFA":r.status==="info"?"#FAFAFA":"#FAFAFA" }}>
                      {r.status==="passed"?"Pass":r.status==="failed"?"Fail":r.status==="info"?"-":"..."}
                    </span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:9, fontWeight:400, color:r.type==="trigger"?"#FAFAFA":r.type==="condition"?"#FAFAFA":r.type==="branch"?"#FAFAFA":T.txt, textTransform:"uppercase", letterSpacing:".04em" }}>
                        {r.type}
                      </span>
                      <span style={{ fontSize:11, fontWeight:400, color:T.txt }}>{r.label}</span>
                    </div>
                    <div style={{ fontSize:10, color:r.status==="failed"?"#FAFAFA":T.tx3, marginTop:2 }}>{r.message}</div>
                  </div>
                  <span style={{ fontSize:9, color:T.tx3, flexShrink:0, marginTop:3 }}>
                    {(() => { const _d = new Date(r.timestamp); let _h = _d.getHours(); const _ap = _h >= 12 ? "PM" : "AM"; _h = _h % 12 || 12; return _h + ":" + String(_d.getMinutes()).padStart(2,"0") + " " + _ap; })()}
                  </span>
                </div>
              ))}

              {running && (
                <div style={{ textAlign:"center", padding:16 }}>
                  <div style={{ fontSize:12, color:T.tx3 }}>Testing steps...</div>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          {done && (
            <div style={{
              marginTop:16, padding:16, borderRadius: 8, textAlign:"center",
              background:overallStatus==="passed"?"rgba(250,250,250,0.04)":"rgba(250,250,250,0.04)",
              border:"1px solid "+(overallStatus==="passed"?"rgba(250,250,250,0.12)":"rgba(250,250,250,0.12)"),
            }}>
              <div style={{ fontSize:14, marginBottom:4, color: overallStatus==="passed" ? "#2E8B57" : "#E53935" }}>{overallStatus==="passed"?"All tests passed":"Tests failed"}</div>
              <div style={{ fontSize:16, fontWeight:400, color:overallStatus==="passed"?"#FAFAFA":"#FAFAFA" }}>
                {overallStatus==="passed"?"All Steps Passed":"Test Failed"}
              </div>
              <div style={{ fontSize:11, color:T.tx3, marginTop:4 }}>
                {results.filter(r=>r.status==="passed").length} passed
                {results.some(r=>r.status==="failed") && <>, {results.filter(r=>r.status==="failed").length} failed</>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 20px", borderTop:"1px solid "+T.bdr, display:"flex", justifyContent:"flex-end", gap:8 }}>
          {done && <Btn onClick={()=>{setDone(false);setResults([]);setOverallStatus(null);}}>Reset</Btn>}
          {!running && !done && <Btn style={{ background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA" }} onClick={runTest}>Run Test</Btn>}
          <Btn onClick={onClose}>{done?"Close":"Cancel"}</Btn>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   LOG VIEWER — Execution history for a workflow
   ══════════════════════════════════════════════════ */
function LogViewer({ workflow, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from("workflow_logs")
          .select("*")
          .eq("org_id", window.__coreflow_org_id)
          .eq("workflow_id", workflow.id)
          .order("started_at", { ascending: false })
          .limit(50);
        setLogs(data || []);
      } catch (e) { console.warn("Log load error:", e.message); }
      setLoading(false);
    })();
  }, [workflow.id]);

  const statusColor = (s) => s === "completed" || s === "test_passed" ? "#FAFAFA" : s === "failed" || s === "test_failed" ? "#FAFAFA" : s === "running" ? "#FAFAFA" : "#FAFAFA";
  const statusIcon = (s) => s === "completed" || s === "test_passed" ? "Pass" : s === "failed" || s === "test_failed" ? "Fail" : "...";
  const statusLabel = (s) => s === "test_passed" ? "Test Passed" : s === "test_failed" ? "Test Failed" : s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ width:720, maxHeight:"85vh", ...crd, display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.bdr, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:9, fontWeight:400, color:"#FAFAFA", letterSpacing:".06em" }}>EXECUTION LOGS</div>
            <div style={{ fontSize:16, fontWeight:400, color:T.txt }}>{workflow.name}</div>
          </div>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            {/* Stats */}
            <div style={{ display:"flex", gap:12 }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:400, color:T.txt }}>{logs.length}</div>
                <div style={{ fontSize:8, color:T.tx3, fontWeight:400 }}>TOTAL</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:400, color:"#FAFAFA" }}>{logs.filter(l=>l.status==="completed"||l.status==="test_passed").length}</div>
                <div style={{ fontSize:8, color:T.tx3, fontWeight:400 }}>PASSED</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:400, color:"#FAFAFA" }}>{logs.filter(l=>l.status==="failed"||l.status==="test_failed").length}</div>
                <div style={{ fontSize:8, color:T.tx3, fontWeight:400 }}>FAILED</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:T.tx3, cursor:"pointer" }}><Icon name="x" size={16} color={T.tx3}/></button>
          </div>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:16 }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:40, color:T.tx3, fontSize:12 }}>Loading logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign:"center", padding:40 }}>
              <div style={{ fontSize:14, fontWeight:400, color:T.txt, marginBottom:4 }}>No logs yet</div>
              <div style={{ fontSize:11, color:T.tx3 }}>Run a test to see execution logs here.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {logs.map(log => {
                const isExpanded = expandedLog === log.id;
                const sc = statusColor(log.status);
                return (
                  <div key={log.id}>
                    {/* Log row */}
                    <div onClick={() => setExpandedLog(isExpanded ? null : log.id)} style={{
                      display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                      borderRadius:isExpanded?"10px 10px 0 0":10, cursor:"pointer",
                      background:isExpanded?sc+"06":"transparent", border:"1px solid "+(isExpanded?sc+"15":T.bdr+"50"),
                    }}>
                      <div style={{
                        width:24, height:24, borderRadius: 8, display:"flex", alignItems:"center", justifyContent:"center",
                        background:sc+"12", border:"1px solid "+sc+"25", flexShrink:0,
                      }}>
                        <span style={{ fontSize:11, color:sc }}>{statusIcon(log.status)}</span>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:11, fontWeight:400, color:T.txt }}>{log.contact_name || "Unknown"}</span>
                          {log.is_test && <span style={{ fontSize:8, padding:"1px 5px", borderRadius:4, background:"#FAFAFA"+"12", color:"#FAFAFA", fontWeight:400 }}>TEST</span>}
                        </div>
                        {log.error_message && <div style={{ fontSize:9, color:"#FAFAFA", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:350 }}>{log.error_step}: {log.error_message.split("|")[0]}</div>}
                      </div>
                      <Badge color={sc}>{statusLabel(log.status)}</Badge>
                      <span style={{ fontSize:9, color:T.tx3, flexShrink:0 }}>{new Date(log.started_at).toLocaleString()}</span>
                      <Icon name={isExpanded?"chevronU":"chevronD"} size={12} color={T.tx3}/>
                    </div>

                    {/* Expanded step detail */}
                    {isExpanded && (
                      <div style={{
                        padding:"12px 14px", borderRadius:"0 0 10px 10px",
                        background:sc+"04", border:"1px solid "+sc+"15", borderTop:"none",
                      }}>
                        {(log.steps_executed || []).length === 0 ? (
                          <div style={{ fontSize:11, color:T.tx3, padding:8 }}>No step details available.</div>
                        ) : (
                          (log.steps_executed || []).map((step, i) => (
                            <div key={i} style={{
                              display:"flex", alignItems:"center", gap:8, padding:"6px 8px",
                              marginLeft:(step.depth||0)*20,
                              borderRadius:6, marginBottom:2,
                              background:step.status==="failed"?"#FAFAFA"+"06":"transparent",
                            }}>
                              <span style={{
                                fontSize:9, width:14, height:14, borderRadius:7, display:"inline-flex", alignItems:"center", justifyContent:"center",
                                background:step.status==="passed"?"#FAFAFA"+"12":step.status==="failed"?"#FAFAFA"+"12":"#FAFAFA"+"12",
                                color:step.status==="passed"?"#FAFAFA":step.status==="failed"?"#FAFAFA":"#FAFAFA",
                              }}>{step.status==="passed" ? <Icon name="check" size={10} color="#FAFAFA" /> : step.status==="failed" ? <Icon name="x" size={10} color="#FAFAFA" /> : <Icon name="arrowR" size={10} color="#FAFAFA" />}</span>
                              <span style={{ fontSize:8, fontWeight:400, color:T.tx3, textTransform:"uppercase", width:45 }}>{step.type}</span>
                              <span style={{ fontSize:10, fontWeight:400, color:T.txt }}>{step.label}</span>
                              <span style={{ fontSize:9, color:step.status==="failed"?"#FAFAFA":T.tx3, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{step.message}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding:"14px 20px", borderTop:"1px solid "+T.bdr, display:"flex", justifyContent:"flex-end" }}>
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}
